import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Modal, Button } from "react-bootstrap";
import { FaTimes, FaRocket, FaCheck } from "react-icons/fa";
import { MdUpdate } from "react-icons/md";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const ChangelogHeader = styled.div`
  background-color: #1F467E;
  color: white;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChangelogTitle = styled.h3`
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BackgroundWrapper = styled.div`
  background: linear-gradient(135deg, #f7f9fc 60%, #e2ecf7);
`;

const ChangelogBody = styled.div`
  padding: 25px;
  max-height: 60vh;
  overflow-y: auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

const UpdateItem = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ccc;
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const UpdateVersion = styled.h4`
  color: #1F467E;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UpdateDate = styled.span`
  font-size: 0.8rem;
  color: #6c757d;
  margin-left: 10px;
`;

const UpdateList = styled.ul`
  padding-left: 20px;
  margin-bottom: 0;
`;

const UpdateListItem = styled.li`
  margin-bottom: 8px;
  position: relative;
  padding-left: 25px;
  line-height: 1.5;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    background-color: #1F467E;
  }
`;

const HighlightBadge = styled.span`
  background-color: #ffc107;
  color: #212529;
  padding: 3px 8px;
  font-size: 0.7rem;
  font-weight: bold;
  margin-left: 10px;
  animation: ${pulse} 2s infinite;
`;

const CustomButton = styled(Button)`
  background-color: #1F467E;
  border: none;
  padding: 10px 20px;
  font-weight: 500;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #15325e;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: rotate(90deg);
  }
`;

const ImageModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  max-width: 90%;
  max-height: 90%;
`;

const CloseImageButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  z-index: 10000;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const FullScreenImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  display: block;
`;

interface ChangelogModalProps {
  show: boolean;
  onClose: () => void;
}

const STORAGE_KEY = 'hasSeenUpdates-9.8.0';

const ChangelogModal: React.FC<ChangelogModalProps> = ({ show, onClose }) => {
  const [viewCount, setViewCount] = useState<number>(0);
  const [shouldShow, setShouldShow] = useState<boolean>(false);

  useEffect(() => {
    const views = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    setViewCount(views);

    if (views < 2) {
      setShouldShow(true);
    }
  }, []);

  const handleConfirm = () => {
    const newCount = viewCount + 1;
    localStorage.setItem(STORAGE_KEY, newCount.toString());
    setViewCount(newCount);
    setShouldShow(false);
    toast.success("Atualizações confirmadas!");
  };

  const updates = [
    {
      version: "9.8.0",
      date: "19/10/2026",
      items:
      [
        "Novo Assistente LocaSmartPro no WhatsApp: vincule seu telefone em 'Minha Conta > Integrações' e consulte locações, locatários, veículos, pagamentos e a posição da frota por mensagem",
        "O Assistente no WhatsApp também cadastra e edita Locatários, Veículos e Lançamentos e dá baixa em parcelas, sempre mostrando o resumo do que será feito e aguardando sua confirmação pelos botões Confirmar / Cancelar",
        "Reforma das Permissões de Usuário: permissões individualizadas por usuário, incluindo os cards do Dashboard e a pesquisa. 'Cadastros > Manutenção > Usuário'",
        "Melhoria na Expansão de Placas: o valor é proporcional aos dias restantes do período pago e o checkout mostra a composição do cálculo",
        "Melhoria na integração com o Asaas para vincular o Locatário ao cliente do Asaas pelo CPF ao criar a locação",
        "Melhoria na integração com o Asaas para escolher, ao criar a locação, quais cobranças já existentes do cliente serão aproveitadas, inclusive os vencimentos de outras assinaturas",
        "Melhoria na integração com o Asaas para cobrar os juros e a multa configurados no lançamento",
        "Caução com nome personalizável na locação",
        "Melhoria no desempenho do Encerramento da Locação, inclusive com assinatura no Asaas",
        "Chave Pix e notificações separadas por Filial",
        "Onboarding guiado para quem está no período de teste e consulta de placa pela tabela FIPE no cadastro de veículo",
        "Importação de Veículos via Planilha agora traz o Tipo do veículo",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: true
    },
    {
      version: "9.7.1",
      date: "24/09/2026",
      items:
      [
        "Melhorias na integração com o Asaas",
        "Melhorias na contagem de veículos no Dashboard",
        "Realizadas melhorias gerais no sistema",
      ],
      highlight: true
    },
    {
      version: "9.7.0",
      date: "21/09/2026",
      items:
      [
        "Melhoria na integração com o Asaas para permitir escolher a forma de pagamento e o parcelamento da caução",
        "Melhoria na integração com o Asaas para decidir se a locação será enviada ao Asaas, sem precisar desativar a integração",
        "Melhoria na integração com o Asaas para maior controle dos lançamentos",
        "Nova Comissão por Locação: uma comissão única por contrato fechado, que pode valer para toda a frota ou para veículos específicos, com a apuração no início, no cadastro, no primeiro pagamento ou no encerramento da locação. 'Cadastros > Manutenção > Usuário > Aba Comissão'",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: true
    },
    {
      version: "9.6.0",
      date: "13/08/2026",
      items:
      [
        "Nova opção de Alterar o Dia de Pagamento das recorrências (Semanal, Mensal e Quinzenal) integrada ao Asaas. 'Movimentos > Operacional > Locação > 3 pontinhos > Alterar Dia de Pagamento'",
        "Ao alterar o dia, é possível gerar automaticamente o Pagamento da Diferença de dias",
        "Novo Dashboard com gráficos por Origem e por Status, clicáveis, levando às telas de Pagamentos, Lançamentos e Multas já filtradas",
        "Criada Ordem de Serviço (OS): criar, listar, editar, cancelar, gerar PDF e converter em Manutenção. 'Movimentos > Operacional > Manutenção'",
        "Incluída Importação de Peças e Serviços via Planilha",
        "Inclusão de Comissão por Filial, sugerida e editável na Locação. 'Aba Valores > Opções Avançadas'",
        "Relatórios de Receitas e Fluxo de Caixa: opção de exibir os valores com a comissão calculada, agrupada por filial",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: true
    },
    {
      version: "9.5.0",
      date: "02/08/2026",
      items:
      [
        "Melhorias no Menu de Ações após criação da Locação",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: true
    },
    {
      version: "9.4.0",
      date: "03/06/2026",
      items:
      [
        "Incluída opção de Link de Afiliação, compartilhe e ganhe 10% de Cashback no PIX!",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: false
    },
    {
      version: "9.3.0",
      date: "14/06/2026",
      items:
      [
        "Criada Opção de Tabs ao abrir telas do Sistema",
        "Incluímos uma IA de assistência dentro do sistema. Basta clicar e conversar",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: false
    },
    {
      version: "9.0.0",
      date: "08/06/2026",
      items:
      [
        "Criada Ferramenta de Filiais: Divisão do Sistema em Empresas",
        "Melhorias nos Relatórios Financeiros: Mais Filtros para melhor gestão",
        "Inclusão de Configuração de Comissão por Usuário",
        "Melhorias na tela de Modelos de Contratos",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: false
    },
    {
      version: "8.6.0",
      date: "22/05/2026",
      items:
      [
        "Dashboard: Inclusão de Totalizador por tipo de veículo",
        "Melhorias nos Relatórios (Exportar em Excel / PDF)",
        "Inclusão da Opção de Exportar Listagem em PDF",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: false
    },
    {
      version: "8.5.0",
      date: "12/05/2026",
      items:
      [
        "Melhorias na tela de Estoque de Peças",
        "Adicionada opção de Sincronismo LocaSmartPro x LocaSmartCheck",
        "Melhorias nos relatórios Financeiros",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: false
    },
    {
      version: "8.4.0",
      date: "26/04/2026",
      items:
      [
        "Campo Identificador no Estoque para organização por categorias (ex: Prateleira A, Filtros)",
        "Parcelamento Inteligente de Multas: ao quitar todas as parcelas, a multa é marcada como Paga automaticamente",
        "Novo modal 'Cobrar Multa do Locatário' redesenhado e mais intuitivo",
        "Filtros por Veículo e Locatário na tela de Multas",
        "Botão 'Ver Financeiro' na tela de Multas para visualizar lançamentos vinculados",
        "Relatório Completo (Laudo) da Locação com seções configuráveis em PDF",
        "Laudo inclui: dados da locação, locatário, veículo, financeiro, multas, manutenções, trocas de óleo e vistorias com fotos",
        "Relatório acessível nos 3 pontinhos da locação, no encerramento e em Relatórios > Geral",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: false
    },
    {
      version: "8.3.0",
      date: "22/04/2026",
      items:
      [
        "Implementada Integração com Plataforma de Rastreio da LocaSmartPro - AgilLock",
        "Plataforma de Rastreio: Plano de Manutenção para Notificação de Manutenções preventivas",
        "Plataforma de Rastreio: Bloqueio / Desbloqueio",
        "Plataforma de Rastreio: Mapa de Veículos",
        "Plataforma de Rastreio: Hodômetro em Tempo real",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho"
      ],
      highlight: false
    },
    {
      version: "8.2.0",
      date: "19/04/2026",
      items: 
      [
        "Realizado ajustes na Integração com Asaas para locação diária",
        "Realizado melhorias nos filtros das telas de Locação, Lançamentos, Multas e Pagamentos",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho"
      ],
      highlight: false
    },
    {
      version: "8.1.0",
      date: "08/04/2026",
      items: [
        "Implementada a opção de parcelamento da caução durante a criação da locação, facilitando a negociação com o cliente",
        "Implementado o cadastro de bancos em 'Cadastros > Financeiro > Bancos', otimizando a conciliação bancária manual",
        "Implementada a funcionalidade de anexar até 4 documentos por locação (ex: aditivos), acessível em 'Movimentos > Operacional > Locação > 3 pontinhos > Documentos'",
        "Adicionadas duas novas variáveis no modelo de contrato: 'Observação' e 'Negociação', proporcionando maior flexibilidade na personalização automática",
        "Realizadas melhorias gerais nos processos internos da plataforma, aumentando a estabilidade e desempenho",
      ],
      highlight: false
    },
    {
      version: "8.0.0",
      date: "05/04/2026",
      items: [
        "Adicionada Integração com o LocaSmartCheck - Sistema de Vistoria Veicular",
        "Melhorias no Módulo de Emissão de NFSe (Beta Teste)",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "7.9.0",
      date: "25/03/2026",
      items: [
        "Adicionado novos campos no Modelo de Contrato (Data Atual, Fipe, Valor do Veículo, Data de Encerramento)",
        "Adicionada opção de Prolongar Locação integrado ao Asaas",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "7.8.0",
      date: "22/03/2026",
      items: [
        "Implementada funcionalidade de Linha do Tempo da Locação. 'Movimentos > Operacional > Locação > 3 pontinhos > Linha do Tempo'",
        "Implementada funcionalidade de gerar pagamentos avulsos para uma locação. 'Movimentos > Financeiro > Pagamentos > Novo Pagamento'",
        "Implementado Assistente LocaSmartPro para auxiliar em dúvidas sobre o sistema",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "7.7.0",
      date: "09/03/2026",
      items: [
        "Implementada funcionalidade de Substituição de Veículo na locação com histórico Completo de Trocas",
        "Implementada funcionalidade de Parcelamento Facilitado de Pagamentos",
        "Implementada funcionalidade que permite baixar a fatura do Asaas ao realizar a baixa manual do pagamento",
        "Implementada funcionalidade de filtro por Vencimento/Pagamento nos Relatórios Financeiros",
        "Implementada funcionalidade de Importar Locatários via Planilha",
        "Implementada funcionalidade de Importar Veículos via Planilha",
        "Melhorias na sincronização de juros e multas com o Asaas ao editar pagamentos",
        "Melhorias na funcionalidade de Estoque integrado ao Financeiro",
        "Melhorias na Geração do Recibo de Pagamento",
        "Melhorias no Editor de Contratos",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "7.6.0",
      date: "03/03/2026",
      items: [
        "Melhorias no processo de cadastro de veículos, agora com preenchimento automático dos campos de marca, modelo e ano com base na placa do veículo",
        "Melhorias na integração com a API FIPE para garantir maior precisão nos valores de mercado dos veículos",
        "Melhorias na criação de Modelos de Contratos",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "7.5.0",
      date: "26/02/2026",
      items: [
        "Inclusão do campo FIPE na tela de Veículos",
        "Melhorias na Integração com o Asaas",
      ],
      highlight: true
    },
    {
      version: "7.4.2",
      date: "14/02/2026",
      items: [
        "Melhorias na integração com o Asaas para permitir cadastro de locações retroativas",
        "Melhoria na rotina de edição de Manutenção com Peças do Estoque",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "7.3.0",
      date: "09/02/2026",
      items: [
        "Melhoria na tela de Usuários. 'Cadastros > Manutenção > Usuários'",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "7.2.0",
      date: "03/02/2026",
      items: [
        "Inclusão de auditoria por usuário, garantindo rastreabilidade completa de todas as movimentações realizadas no sistema.",
        "Liberada Emissão de NFSe para Empresas selecionadas (Beta Teste)",
      ],
      highlight: false
    },
    {
      version: "7.1.0",
      date: "24/01/2026",
      items: [
        "Disponibilizado APP do Locador para Iphone e Android",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "7.0.0",
      date: "16/01/2026",
      items: [
        "Incluída Opção de Ações em Lote na tela de Lançamentos Financeiros. 'Movimentos > Financeiro > Lançamentos'",
        "Melhoria na ferramenta de Exportação de dados",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "6.9.0",
      date: "02/01/2026",
      items: [
        "Implementada Integração com Autentique para assinatura digital direto pelo sistema! Acesse Seu Perfil > Integrações",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "6.8.0",
      date: "24/12/2025",
      items: [
        "Foi criado um Hub de Treinamento completo da Plataforma! Clique no botão 'Assistir Treinamento'",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "6.7.0",
      date: "16/12/2025",
      items: [
        "Foram incluídos novos filtros na tela de Manutenção, permitindo agora a consulta por serviços realizados e peças utilizadas. 'Movimentos > Operacional > Manutenção'",
        "Foram incluídos novos filtros na tela de Locação, permitindo agora a consulta por Locatário ou Veículo. 'Movimentos > Operacional > Locação'",
        "Foram implementadas otimizações na plataforma com foco em desempenho e velocidade",
      ],
      highlight: false
    },
    {
      version: "6.6.0",
      date: "14/12/2025",
      items: [
        "Incluída opção 'Ações em Lote' na tela de Pagamentos. 'Movimentos > Operacional > Pagamentos'",
        "Foram implementadas otimizações na plataforma com foco em desempenho e velocidade",
      ],
      highlight: false
    },
    {
      version: "6.5.0",
      date: "07/12/2025",
      items: [
        "Incluída a Integração com o Asaas. Acesse 'Seu perfil > Integrações'",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "6.4.0",
      date: "11/11/2025",
      items: [
        "Incluída opção para geração de OS. 'Movimentos > Operacional > Manutenção'",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "6.3.0",
      date: "06/11/2025",
      items: [
        "Incluída opção de Recibo de Vistoria Veicular. 'Movimentos > Operacional > Vistoria'",
        "Incluída opção de Recibo de Pagamentos. 'Movimentos > Financeiro > Pagamentos'",
      ],
      highlight: false
    },
    {
      version: "6.2.0",
      date: "04/11/2025",
      items: [
        "Disponibilizado APP para o Locatário, acesse 'Cadastros > Locação > Locatário'",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "6.1.0",
      date: "19/10/2025",
      items: [
        "Implementada tela para realização de vistorias veiculares",
        "Implementada tela para registro de trocas de óleo",
        "Adicionado campo Chassi na configuração de contrato",
        "Adicionado campo Fiador na configuração de contrato",
        "Adicionado campo Número da CNH na configuração de contrato",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "6.0.0",
      date: "16/09/2025",
      items: [
        'Implementada opção de Planos Trimestral, Semestral e Anual com descontos progressivos',
        'Adicionado opção de incluir Telefone e Email do Locatário no contrato automático',
        'Incluída opção de trazer apenas Pendentes no relatório de Despesas',
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "5.9.0",
      date: "08/09/2025",
      items: [
        'Implementada a funcionalidade de geração automática de contratos. Acesse: "Cadastros > Locação > Contrato"',
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "5.7.0",
      date: "21/08/2025",
      items: [
        "Central de notificações implementada",
        "Funcionalidade para indicar a venda de veículo ao encerrar locação",
        "Acesso rápido adicionado ao menu lateral",
        "Possibilidade de locar mais de um veículo para o mesmo locatário",
        "Cadastro de locatário Pessoa Jurídica (PJ) disponível",
        "Campo para Observação ao encerrar locação",
        "Opção de inativar veículo adicionada",
        "Melhorias nos processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "5.6.0",
      date: "06/08/2025",
      items: [
        "Implementada funcionalidade para parcelamento de manutenções",
        "Implementada opção para definir data de pagamento da manutenção",
        "Implementada opção para definir data de pagamento de um lançamento",
        "Implementada a opção de criar automaticamente o lançamento ao registrar a entrada de peças no estoque",
        "Refeita tela de confirmação de pagamentos",
        "Criada tela para visualização de Fatura da mensalidade",
        "Melhorias em processos internos da plataforma",
      ],
      highlight: false
    },
    {
      version: "5.5.0",
      date: "22/07/2025",
      items: [
        "Implementada funcionalidade de cobrar via WhatsApp",
        "Implementada funcionalidade de gerar Chave PIX para pagamento",
        "Implementada funcionalidade de filtro personalizado no Dashboard",
        "Implementado botão para visualização rápida de pendências",
        "Implementado gráfico de Lucro x Veículo",
        "Implementado gráfico de Faturamento x Veículo",
        "Implementado gráfico de Custo x Veículo",
        "Melhorias na usuabilidade geral do sistema",
      ],
      highlight: false
    },
    {
      version: "5.4.0",
      date: "08/07/2025",
      items: [
        "Implementação da funcionalidade de Exportar para Planilha com controle de Colunas",
        "Os campos de marca, modelo, estado e cidade agora permitem preenchimento livre",
      ],
      highlight: false
    },
    {
      version: "5.3.0",
      date: "06/07/2025",
      items: [
        "Implementação da funcionalidade de Locação Simplificada",
        "Inclusa opção de atualizar o valor do aluguel preservando os pagamentos anteriores",
        "Melhorias aplicadas no processo de edição de locações",
        "Melhorias na usuabilidade geral do sistema",
      ],
      highlight: false
    },
    {
      version: "5.2.0",
      date: "02/07/2025",
      items: [
        "Adicionado totalizadores na Tela de Pagamentos",
        "Adicionado totalizadores na Tela de Lançamentos",
        "Adicionado Calendário de Recebimentos no Dashboard",
        "Adicionado Indicador de Multas no Dashboard",
        "Adicionado Indicador de Despesas a Vencer no Dashboard",
        "Adicionado Indicador de Despesas Vencidas no Dashboard",
        "Adicionado Indicador de Diferença de Entradas e Saídas no Dashboard",
        "Melhorias na usuabilidade geral do sistema",
      ],
      highlight: false
    },
    {
      version: "5.1.0",
      date: "23/06/2025",
      items: [
        "Adicionado suporte para anexar documentos e imagens do veículo",
        "Adicionado suporte para anexar o contrato de locação",
        "Adicionado suporte para anexar a CNH do locatário",
        "Agora é possível anexar recibos, garantias e imagens em manutenções",
        "Agora é possível anexar imagens e arquivos em lançamentos financeiros",
        "Adicionada opção para anexar sua logo na aba 'Minha conta'; ela será exibida nos relatórios"
      ],
    },
    {
      version: "5.0.2",
      date: "12/06/2025",
      items: [
        "Melhorias na responsividade para dispositivos móveis",
        "Melhorias na usuabilidade da Plataforma",
        "Atualização da biblioteca de segurança"
      ]
    },
  ];

  return (
    <>
      <Modal
        show={show && shouldShow}
        onHide={onClose}
        centered
        size="lg"
        backdrop="static"
      >
        <ChangelogHeader>
          <ChangelogTitle>
            <MdUpdate /> Novidades no LocaSmartPro
          </ChangelogTitle>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </ChangelogHeader>

        <BackgroundWrapper>
          <ChangelogBody>
            <p style={{ marginBottom: '25px', fontSize: '1.1rem' }}>
              Locador, confira as últimas atualizações e melhorias que preparamos para você:
            </p>

            {updates.map((update, index) => (
              <UpdateItem key={index}>
                <UpdateVersion>
                  <FaRocket /> Versão {update.version}
                  {update.highlight && <HighlightBadge>NOVO</HighlightBadge>}
                  <UpdateDate>{update.date}</UpdateDate>
                </UpdateVersion>
                <UpdateList>
                  {update.items.map((item, itemIndex) => (
                    <UpdateListItem key={itemIndex}>{item}</UpdateListItem>
                  ))}
                </UpdateList>
              </UpdateItem>
            ))}
          </ChangelogBody>
        </BackgroundWrapper>

        <Modal.Footer style={{ justifyContent: 'right', borderTop: 'none' }}>
          <CustomButton onClick={handleConfirm}>
            <FaCheck /> Entendi as novidades
          </CustomButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChangelogModal;